import { DestroyRef, Injectable, effect, inject, signal, untracked } from '@angular/core';
import { Observable, Subject, Subscription, finalize } from 'rxjs';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { CoreTargetService } from 'src/app/core/services/target.service';
import { SupportApiService } from '../services/support-api.service';
import { SupportChatLauncherService } from '../services/support-chat-launcher.service';
import { SupportChatService } from '../services/support-chat.service';
import { SUPPORT_API } from '../support-system.config';
import { SupportRequestSummary } from '../support-system.models';

/** How long the launcher says "Request done". */
const DONE_FLASH_MS = 5_000;
/** The last Done the launcher announced, per user. */
const SEEN_DONE_KEY = 'supportRequestsSeenDoneAt';
/** While the modal is open (the backend asks for no less than 20 s). */
const MODAL_POLL_MS = 30_000;

/**
 * The launcher's state: how many requests are open, and "Request done" for
 * a few seconds when one was finished since the last look. Runs while a
 * chat-box is on screen for a client account: every 5 minutes, and every
 * 30 seconds while the Requests modal is open (nothing is pushed, and a
 * request can be finished, or a scheduled pause run, while it's on screen).
 */
@Injectable({ providedIn: 'root' })
export class SupportRequestsService {
  private api = inject(SupportApiService);
  private chat = inject(SupportChatService);
  private launcher = inject(SupportChatLauncherService);
  private targetService = inject(CoreTargetService);
  private storage = inject(LocalStorageService);

  readonly openCount = signal(0);
  readonly justDone = signal(false);

  /** The count or the latest Done moved: what My requests shows is stale. */
  private readonly changedSubject = new Subject<void>();
  readonly changed$: Observable<void> = this.changedSubject.asObservable();

  private request?: Subscription;
  private busy = false;
  /** The last answer, to tell whether anything changed (per shop). */
  private last: { target?: string; open: number; lastDoneAt: string | null } | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private modalTimer: ReturnType<typeof setInterval> | null = null;
  private doneTimer: ReturnType<typeof setTimeout> | null = null;
  private lastRefresh = 0;
  private readonly onVisible = () => {
    const every = this.modalTimer ? MODAL_POLL_MS : SUPPORT_API.requestSummaryMs;
    if (document.visibilityState === 'visible' && Date.now() - this.lastRefresh >= every) {
      this.refresh();
    }
  };

  constructor() {
    effect(() => {
      const on = this.launcher.available();
      untracked(() => (on ? this.start() : this.stop()));
    });
    inject(DestroyRef).onDestroy(() => this.stop());
  }

  /** The selected shop if it's one of the user's, else their default. */
  target(): string | undefined {
    return this.chat.targetFor(this.targetService.getSelectedTarget('target') || '');
  }

  /** `skipIfBusy`: a timer tick, which waits for a check still running. */
  refresh(skipIfBusy = false): void {
    if (skipIfBusy && this.busy) return;
    this.lastRefresh = Date.now();
    this.request?.unsubscribe();
    const target = this.target();
    this.busy = true;
    this.request = this.api
      .requestSummary(target)
      .pipe(finalize(() => (this.busy = false)))
      .subscribe({
        next: (summary) => {
          const open = summary.open || 0;
          const lastDoneAt = summary.lastDoneAt || null;
          // Another shop starts over: nothing to compare with.
          const changed =
            !!this.last &&
            this.last.target === target &&
            (this.last.open !== open || this.last.lastDoneAt !== lastDoneAt);
          this.last = { target, open, lastDoneAt };
          this.openCount.set(open);
          this.announceDone(summary);
          if (changed) this.changedSubject.next();
        },
        // The badge is a hint: keep the last count, try again next time.
        error: () => undefined,
      });
  }

  /** The Requests modal opened: check every 30 s while the tab is visible. */
  watchModal(): void {
    if (this.modalTimer) return;
    this.modalTimer = setInterval(() => {
      if (document.visibilityState === 'visible') this.refresh(true);
    }, MODAL_POLL_MS);
  }

  unwatchModal(): void {
    if (this.modalTimer) clearInterval(this.modalTimer);
    this.modalTimer = null;
  }

  private start(): void {
    if (this.timer) return;
    this.refresh();
    this.timer = setInterval(() => {
      if (document.visibilityState === 'visible') this.refresh();
    }, SUPPORT_API.requestSummaryMs);
    document.addEventListener('visibilitychange', this.onVisible);
  }

  private stop(): void {
    this.request?.unsubscribe();
    this.unwatchModal();
    this.last = null;
    if (this.timer) clearInterval(this.timer);
    if (this.doneTimer) clearTimeout(this.doneTimer);
    this.timer = this.doneTimer = null;
    this.justDone.set(false);
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  /**
   * A request was marked Done since the last one announced. On the very
   * first check there is nothing to compare with: it only remembers.
   */
  private announceDone({ lastDoneAt }: SupportRequestSummary): void {
    const key = `${SEEN_DONE_KEY}:${this.chat.me()?.id || ''}`;
    const seen = this.storage.get<string>(key);
    const latest = lastDoneAt || '';
    if (seen === null) {
      this.storage.set(key, latest);
      return;
    }
    if (!latest || latest <= seen) return;
    this.storage.set(key, latest);
    this.justDone.set(true);
    if (this.doneTimer) clearTimeout(this.doneTimer);
    this.doneTimer = setTimeout(() => this.justDone.set(false), DONE_FLASH_MS);
  }
}
